import {useQuery, useQueryClient} from '@tanstack/react-query';
import {api} from '@/shared/services/api-client';
import {fetchAllSchedules, type ScheduleListParams} from '@/shared/services/scheduleRetrieval';
import {queryKeys} from '@/lib/query-keys';
import {PROJECT_SCHEDULES_STALE_TIME_MS} from '@/config/cacheTtl';

// Moderators-list / session-types-by-project queries moved to
// @/shared/hooks/useReferenceListQueries so features other than `schedules`
// can use the same cached query (the no-restricted-imports architecture rule
// blocks cross-feature imports) — re-exported here so existing callers of
// this module don't need to change their import path.
export {
  moderatorsListQueryKey,
  fetchModeratorsList,
  useModeratorsListQuery,
  sessionTypesDetailedByProjectQueryKey,
  fetchDetailedSessionTypesByProject,
  useDetailedSessionTypesByProjectQuery,
} from '@/shared/hooks/useReferenceListQueries';

function isUsableProjectId(projectId: string | undefined | null): projectId is string {
  return !!projectId && projectId !== 'all';
}

export function tasksByProjectQueryKey(projectId: string) {
  return queryKeys.tasks.byProject(projectId);
}

/** OneForma tasks available to attach to a session type, for a project. */
export async function fetchTasksByProject(projectId: string): Promise<any[]> {
  const res = await api.tasks.byProject(projectId);
  return (res.data as any)?.data || res.data || [];
}

export function useTasksByProjectQuery(
  projectId: string | undefined | null,
  options?: {enabled?: boolean},
) {
  const usable = isUsableProjectId(projectId);
  return useQuery({
    queryKey: tasksByProjectQueryKey(usable ? projectId : ''),
    queryFn: () => fetchTasksByProject(projectId as string),
    enabled: (options?.enabled ?? true) && usable,
  });
}

export function jobsByProjectQueryKey(projectId: string) {
  return queryKeys.jobs.byProject(projectId);
}

/** OneForma jobs available to attach to a session type, for a project. */
export async function fetchJobsByProject(projectId: string): Promise<any[]> {
  const res = await api.sessionTypes.jobsByProject(projectId);
  const jobsData = (res.data as any)?.data || [];
  return jobsData.map((job: any) => ({
    job_id: String(job.job_id),
    job_name: job.job_name || String(job.job_id),
  }));
}

export function useJobsByProjectQuery(
  projectId: string | undefined | null,
  options?: {enabled?: boolean},
) {
  const usable = isUsableProjectId(projectId);
  return useQuery({
    queryKey: jobsByProjectQueryKey(usable ? projectId : ''),
    queryFn: () => fetchJobsByProject(projectId as string),
    enabled: (options?.enabled ?? true) && usable,
  });
}

export function moderatorAvailabilityByProjectQueryKey(projectId: string) {
  return queryKeys.moderatorAvailability.byProject(projectId);
}

/** Moderator availability records for a project (unfiltered by active/upcoming status). */
export async function fetchModeratorAvailabilityByProject(
  projectId: string,
): Promise<any[]> {
  const res = await api.moderatorAvailability.list({project_id: projectId});
  return (res.data as any)?.data || [];
}

export function useModeratorAvailabilityByProjectQuery(
  projectId: string | undefined | null,
  options?: {enabled?: boolean},
) {
  return useQuery({
    queryKey: moderatorAvailabilityByProjectQueryKey(projectId ?? ''),
    queryFn: () => fetchModeratorAvailabilityByProject(projectId as string),
    enabled: (options?.enabled ?? true) && !!projectId,
  });
}

export function weeklySchedulesByProjectQueryKey(projectId: string) {
  return queryKeys.schedules.weeklyByProject(projectId);
}

/**
 * Raw (unfiltered by active/upcoming status) schedules for a project, paged
 * through in full. Callers apply their own status filtering (e.g.
 * `isScheduleActive`) since that's scheduler-feature business logic.
 */
export async function fetchWeeklySchedulesByProject(
  projectId: string,
): Promise<any[]> {
  const params: ScheduleListParams = {project_id: projectId};
  const res = await fetchAllSchedules(params);
  return res.data?.schedules ?? [];
}

export function useWeeklySchedulesByProjectQuery(
  projectId: string | undefined | null,
  options?: {enabled?: boolean},
) {
  return useQuery({
    queryKey: weeklySchedulesByProjectQueryKey(projectId ?? ''),
    queryFn: () => fetchWeeklySchedulesByProject(projectId as string),
    enabled: (options?.enabled ?? true) && !!projectId,
  });
}

/**
 * Schedule data is more live than reference data (sessions get booked and
 * cancelled in real time), but short enough to dedupe same-tab-visit /
 * cross-consumer refetches — same reasoning as ACTION_CENTER_STALE_TIME_MS in
 * useActionCenterDataQuery.ts. Set explicitly rather than relying on the
 * QueryClient's own default so this fetcher's behavior doesn't silently shift
 * if that global default ever changes.
 */

/**
 * Imperative counterpart to `useWeeklySchedulesByProjectQuery`, for callers
 * outside a `useQuery` (imperative page effects, other features' fetcher
 * hooks) that still need the *same* full, unscoped-by-moderator project
 * schedule list. Shares the identical query key, so whichever of the two
 * fetches first populates the cache the other reads from — this is the
 * shared cache that `Schedule.tsx`'s own page-level fetch and the Action
 * Center badge/page (features/projects) both need, instead of each pulling
 * the full schedule list independently.
 */
export function useProjectSchedulesFetcher() {
  const queryClient = useQueryClient();

  const fetchProjectSchedules = (projectId: string, options?: {refresh?: boolean}) =>
    queryClient.fetchQuery({
      queryKey: weeklySchedulesByProjectQueryKey(projectId),
      queryFn: () => fetchWeeklySchedulesByProject(projectId),
      staleTime: options?.refresh ? 0 : PROJECT_SCHEDULES_STALE_TIME_MS,
    });

  return {fetchProjectSchedules};
}
